import { normalizeCourseTitle } from './pathwayHelpers';

function buildSubjectAreaUrl(course) {
  const discipline = String(course?.discipline || '').trim().toLowerCase();
  const disciplineSlugMap = {
    leadership: 'leadership',
    strategy: 'strategy',
    finance: 'finance',
    marketing: 'marketing',
    'general management': 'general-management',
    'digital transformation and innovation': 'digital-transformation-and-innovation',
    'human resources': 'human-resources'
  };

  const disciplineSlug = disciplineSlugMap[discipline];
  if (disciplineSlug) {
    return `https://www.london.edu/executive-education/${disciplineSlug}`;
  }

  if (course?.sourceUrl && /^https:\/\/www\.london\.edu\/executive-education\//i.test(course.sourceUrl)) {
    const segments = course.sourceUrl.split('/').filter(Boolean);
    const execEdIndex = segments.findIndex((segment) => segment === 'executive-education');
    if (execEdIndex >= 0 && segments[execEdIndex + 1]) {
      return `https://www.london.edu/executive-education/${segments[execEdIndex + 1]}`;
    }
  }

  return 'https://www.london.edu/executive-education';
}

function escapeHtml(value = '') {
  return String(value || '')
    .replace(/&/g, '&amp;')
    .replace(/</g, '&lt;')
    .replace(/>/g, '&gt;')
    .replace(/"/g, '&quot;')
    .replace(/'/g, '&#39;');
}

function getProgrammeSlot(programme, certificationPath) {
  if (certificationPath === 'CIF') {
    return programme.isFreeChoice ? 'extension/free-choice slot' : 'finance core slot';
  }

  if (programme.isFreeChoice) {
    return 'free-choice slot';
  }

  if (programme.cimCategory) {
    return `${programme.cimCategory} pillar`;
  }

  return 'recommended programme';
}

export function buildDraftEmail(pathway, recommendations, certificationPath, snapshot, goals, courseCatalogue = []) {
  const selectedPathways = Array.isArray(pathway) ? pathway.filter(Boolean) : pathway ? [pathway] : [];
  const primaryPathway = selectedPathways[0] || null;
  const hasAlternativePathways = selectedPathways.length > 1;
  const certificationLabel = certificationPath === 'CIF'
    ? 'Certificate in Finance (CIF)'
    : certificationPath === 'CIM'
      ? 'Certificate in Management (CIM)'
      : 'executive education';

  const profileHighlights = snapshot
    .filter((entry) => ['Career Level', 'Career Levels', 'Sector', 'Management Experience', 'Primary Objective', 'Current Challenge', 'Format Preference', 'Completion Window', 'Already Taken at LBS'].includes(entry.label))
    .slice(0, 6)
    .map((entry) => `- ${entry.label}: ${entry.value}`);

  const lines = [
    'Subject: Recommended London Business School Executive Education pathway',
    '',
    'Dear [Name],',
    '',
    `Following our review of your profile and development priorities, I have outlined a recommended London Business School Executive Education pathway for your consideration.`,
    '',
    `The proposed sequence is designed to support your stated objectives while keeping the ${certificationLabel} requirements and practical delivery considerations in view.`
  ];

  if (profileHighlights.length > 0) {
    lines.push('');
    lines.push('Profile basis for the recommendation:');
    lines.push(...profileHighlights);
  }

  if ((goals || '').trim()) {
    lines.push('');
    lines.push('Learning context shared:');
    lines.push(goals.trim());
  }

  if (hasAlternativePathways) {
    lines.push('');
    lines.push(`Recommended pathway options: ${selectedPathways.map((option) => option.label).join(', ')}`);
    lines.push('I have framed these as credible alternatives so we can position the strongest primary route while keeping practical fallbacks available if timing, emphasis, or format preferences change.');
    selectedPathways.forEach((option, index) => {
      lines.push('');
      lines.push(`Option ${index + 1}: ${option.label}`);
      if (option.summary) {
        lines.push(option.summary);
      }
      if (option.tradeoffs) {
        lines.push(`Positioning consideration: ${option.tradeoffs}`);
      }
    });
  } else if (primaryPathway) {
    lines.push('');
    lines.push(`Recommended pathway: ${primaryPathway.label}`);
    if (primaryPathway.summary) {
      lines.push(primaryPathway.summary);
    }
    if (primaryPathway.tradeoffs) {
      lines.push(`A key consideration is that ${primaryPathway.tradeoffs}`);
    }
  }

  if (recommendations.length > 0) {
    lines.push('');
    if (hasAlternativePathways) {
      lines.push('Recommended programme options:');
      selectedPathways.forEach((option, optionIndex) => {
        lines.push('');
        lines.push(`Option ${optionIndex + 1}: ${option.label}`);
        (option.programmes || []).forEach((programme, programmeIndex) => {
          const matchedCourse = courseCatalogue.find((course) => normalizeCourseTitle(course.title) === normalizeCourseTitle(programme.title));
          lines.push(`${programmeIndex + 1}. ${programme.title}`);
          lines.push(`   Role in pathway: ${getProgrammeSlot(programme, certificationPath)}`);
          if ((programme.reason || '').trim()) {
            lines.push(`   Why recommended: ${programme.reason.trim()}`);
          }
          if (matchedCourse?.sourceUrl) {
            lines.push(`   Programme page: ${matchedCourse.sourceUrl}`);
          }
          if (matchedCourse) {
            lines.push(`   Explore more in this subject area: ${buildSubjectAreaUrl(matchedCourse)}`);
          }
        });
      });
    } else {
      lines.push('Recommended programme sequence:');
      recommendations.forEach((programme, index) => {
        const matchedCourse = courseCatalogue.find((course) => normalizeCourseTitle(course.title) === normalizeCourseTitle(programme.title));
        lines.push('');
        lines.push(`${index + 1}. ${programme.title}`);
        lines.push(`   Role in pathway: ${getProgrammeSlot(programme, certificationPath)}`);
        if ((programme.reason || '').trim()) {
          lines.push(`   Why recommended: ${programme.reason.trim()}`);
        }
        if (matchedCourse?.sourceUrl) {
          lines.push(`   Programme page: ${matchedCourse.sourceUrl}`);
        }
        if (matchedCourse) {
          lines.push(`   Explore more in this subject area: ${buildSubjectAreaUrl(matchedCourse)}`);
        }
      });
    }
  }

  lines.push('');
  lines.push(hasAlternativePathways
    ? 'Together, these options give us a useful set of alternatives: one can be positioned as the primary recommendation, while the others remain strong fallbacks if timing, preferred emphasis, or availability changes.'
    : 'Together, these programmes provide a structured progression from core capability building to applied leadership and decision-making impact. The recommended sequence is designed to help translate the learning into immediate workplace value while supporting longer-term professional development.');
  lines.push('');
  lines.push('I would be happy to talk through the recommendation, answer any questions, and help refine the pathway around timing, preferred format, and availability.');
  lines.push('');
  lines.push('Best regards,');
  lines.push('[Your Name]');

  return lines.join('\n');
}

export function buildDraftEmailHtml(pathway, recommendations, certificationPath, snapshot, goals, courseCatalogue = []) {
  const selectedPathways = Array.isArray(pathway) ? pathway.filter(Boolean) : pathway ? [pathway] : [];
  const primaryPathway = selectedPathways[0] || null;
  const hasAlternativePathways = selectedPathways.length > 1;
  const certificationLabel = certificationPath === 'CIF'
    ? 'Certificate in Finance (CIF)'
    : certificationPath === 'CIM'
      ? 'Certificate in Management (CIM)'
      : 'executive education';

  const profileHighlights = snapshot
    .filter((entry) => ['Career Level', 'Career Levels', 'Sector', 'Management Experience', 'Primary Objective', 'Current Challenge', 'Format Preference', 'Completion Window', 'Already Taken at LBS'].includes(entry.label))
    .slice(0, 6);

  const sections = [
    '<p>Dear [Name],</p>',
    `<p>Following our review of your profile and development priorities, I have outlined a recommended London Business School Executive Education pathway for your consideration.</p>`,
    `<p>The proposed sequence is designed to support your stated objectives while keeping the ${escapeHtml(certificationLabel)} requirements and practical delivery considerations in view.</p>`
  ];

  if (profileHighlights.length > 0) {
    sections.push('<p><strong>Profile basis for the recommendation:</strong></p>');
    sections.push(`<ul>${profileHighlights.map((entry) => `<li><strong>${escapeHtml(entry.label)}:</strong> ${escapeHtml(entry.value)}</li>`).join('')}</ul>`);
  }

  if ((goals || '').trim()) {
    sections.push('<p><strong>Learning context shared:</strong></p>');
    sections.push(`<p>${escapeHtml(goals.trim())}</p>`);
  }

  if (hasAlternativePathways) {
    sections.push(`<p><strong>Recommended pathway options:</strong> ${escapeHtml(selectedPathways.map((option) => option.label).join(', '))}</p>`);
    sections.push('<p>I have framed these as credible alternatives so we can position the strongest primary route while keeping practical fallbacks available if timing, emphasis, or format preferences change.</p>');
    const pathwayOptionItems = selectedPathways.map((option) => {
      const parts = [
        `<strong>${escapeHtml(option.label)}</strong>`,
        option.summary ? `<div>${escapeHtml(option.summary)}</div>` : '',
        option.tradeoffs ? `<div>Positioning consideration: ${escapeHtml(option.tradeoffs)}</div>` : ''
      ].filter(Boolean);
      return `<li>${parts.join('')}</li>`;
    }).join('');
    sections.push(`<ol>${pathwayOptionItems}</ol>`);
  } else if (primaryPathway) {
    sections.push(`<p><strong>Recommended pathway:</strong> ${escapeHtml(primaryPathway.label)}</p>`);
    if (primaryPathway.summary) {
      sections.push(`<p>${escapeHtml(primaryPathway.summary)}</p>`);
    }
    if (primaryPathway.tradeoffs) {
      sections.push(`<p>A key consideration is that ${escapeHtml(primaryPathway.tradeoffs)}</p>`);
    }
  }

  if (recommendations.length > 0) {
    sections.push(`<p><strong>${hasAlternativePathways ? 'Recommended programme options' : 'Recommended programme sequence'}:</strong></p>`);
    if (hasAlternativePathways) {
      const optionItems = selectedPathways.map((option, optionIndex) => {
        const programmeItems = (option.programmes || []).map((programme, programmeIndex) => {
          const matchedCourse = courseCatalogue.find((course) => normalizeCourseTitle(course.title) === normalizeCourseTitle(programme.title));
          const parts = [
            `<strong>${programmeIndex + 1}. ${escapeHtml(programme.title)}</strong>`,
            `<div>Role in pathway: ${escapeHtml(getProgrammeSlot(programme, certificationPath))}</div>`
          ];

          if ((programme.reason || '').trim()) {
            parts.push(`<div>Why recommended: ${escapeHtml(programme.reason.trim())}</div>`);
          }
          if (matchedCourse?.sourceUrl) {
            parts.push(`<div>Programme page: <a href="${escapeHtml(matchedCourse.sourceUrl)}">${escapeHtml(matchedCourse.sourceUrl)}</a></div>`);
          }
          if (matchedCourse) {
            const subjectAreaUrl = buildSubjectAreaUrl(matchedCourse);
            parts.push(`<div>Explore more in this subject area: <a href="${escapeHtml(subjectAreaUrl)}">${escapeHtml(subjectAreaUrl)}</a></div>`);
          }

          return `<li>${parts.join('')}</li>`;
        }).join('');

        return `<li><strong>Option ${optionIndex + 1}: ${escapeHtml(option.label)}</strong><ol>${programmeItems}</ol></li>`;
      }).join('');
      sections.push(`<ol>${optionItems}</ol>`);
    } else {
      const items = recommendations.map((programme, index) => {
        const matchedCourse = courseCatalogue.find((course) => normalizeCourseTitle(course.title) === normalizeCourseTitle(programme.title));
        const parts = [
          `<strong>${index + 1}. ${escapeHtml(programme.title)}</strong>`,
          `<div>Role in pathway: ${escapeHtml(getProgrammeSlot(programme, certificationPath))}</div>`
        ];

        if ((programme.reason || '').trim()) {
          parts.push(`<div>Why recommended: ${escapeHtml(programme.reason.trim())}</div>`);
        }
        if (matchedCourse?.sourceUrl) {
          parts.push(`<div>Programme page: <a href="${escapeHtml(matchedCourse.sourceUrl)}">${escapeHtml(matchedCourse.sourceUrl)}</a></div>`);
        }
        if (matchedCourse) {
          const subjectAreaUrl = buildSubjectAreaUrl(matchedCourse);
          parts.push(`<div>Explore more in this subject area: <a href="${escapeHtml(subjectAreaUrl)}">${escapeHtml(subjectAreaUrl)}</a></div>`);
        }

        return `<li>${parts.join('')}</li>`;
      }).join('');
      sections.push(`<ol>${items}</ol>`);
    }
  }

  sections.push(hasAlternativePathways
    ? '<p>Together, these options give us a useful set of alternatives: one can be positioned as the primary recommendation, while the others remain strong fallbacks if timing, preferred emphasis, or availability changes.</p>'
    : '<p>Together, these programmes provide a structured progression from core capability building to applied leadership and decision-making impact. The recommended sequence is designed to help translate the learning into immediate workplace value while supporting longer-term professional development.</p>');
  sections.push('<p>I would be happy to talk through the recommendation, answer any questions, and help refine the pathway around timing, preferred format, and availability.</p>');
  sections.push('<p>Best regards,<br/>[Your Name]</p>');

  return sections.join('');
}

export function plainTextEmailToHtml(value = '') {
  const paragraphs = String(value || '')
    .split(/\n{2,}/)
    .map((paragraph) => paragraph.trim())
    .filter(Boolean)
    .map((paragraph) => `<p>${escapeHtml(paragraph).replace(/\n/g, '<br/>')}</p>`);

  return paragraphs.join('');
}
